"use server";

import { revalidatePath } from "next/cache";
import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import { z } from "zod";
import { getCustomerSession } from "@/lib/auth/customer-session";
import { isProductOrderableNow } from "@/lib/commerce/availability";
import { availableStocks, stockKey } from "@/lib/commerce/branches";
import {
	CART_COOKIE,
	mergeLine,
	parseCartCookie,
	storedLineKey,
	writeCartCookie,
} from "@/lib/commerce/cart-cookie";
import { MAX_LINE_QTY } from "@/lib/commerce/cart-qty";
import {
	addonGroups,
	addonSelectionInvalid,
	MAX_LINE_ADDONS,
} from "@/lib/commerce/product-options";
import { getStorefrontBranchContext } from "@/lib/tenant/branch-context";
import { getCurrentTenant } from "@/lib/tenant/resolve";

import { OrderItemAddonsSchema } from "@/lib/validation/json";

/**
 * The cart's four mutations, as server actions over FormData (TRY-128).
 *
 * ## Why FormData and not a typed argument
 *
 * So a plain `<form action={…}>` can post them with **no JavaScript**. That is the
 * whole point of the change: the cart used to live in `localStorage`, which made a
 * no-JS order impossible at the root — `/checkout` rendered "Your cart is empty" to a
 * `curl` because there was no cart to render. Every control that changes the cart is
 * now a form, and the client provider posts the same FormData through the same action
 * rather than keeping its own copy of the truth.
 *
 * ## Three rules for anything added here
 *
 * - **Write the cookie, then `revalidatePath`.** The server components that render
 *   the cart read the cookie, so without the revalidate a no-JS submit lands back on
 *   a cached page showing the old cart, which reads as the click having done nothing.
 * - **`viewOnly` refuses an ADD and permits a removal.** A browse-only store takes no
 *   orders, but a customer must still be able to empty a cart built before the
 *   merchant flipped the switch — the same asymmetry `CartContext` documented and
 *   `/api/cart/track` depends on.
 * - **Never trust a price, a name or an image from the client.** They are not in the
 *   cookie at all; `resolveCartLines` recomputes them through `computeOrder`.
 */

/**
 * `0` means remove: a stepper reaching zero is a removal, not an error. Past
 * the cap it clamps rather than refuses, so a typed 1500 lands as the cap.
 */
const qtySchema = z.coerce
	.number()
	.int()
	.min(0)
	.transform((n) => Math.min(n, MAX_LINE_QTY));

const addonsSchema = z
	.array(z.object({ gi: z.number().int().min(0).max(99), ci: z.number().int().min(0).max(99) }))
	.max(MAX_LINE_ADDONS);

function str(fd: FormData, key: string): string {
	const v = fd.get(key);
	return typeof v === "string" ? v.trim() : "";
}

/**
 * Where a no-JS submit goes back to.
 *
 * ⚠️ Validated against a leading `/` and no `//` or backslash — a `back` read straight
 * from the form is an open redirect, and this one is posted by an unauthenticated
 * shopper. `linkTargetField`'s reasoning, applied to a control value rather than to
 * stored copy.
 *
 * ⚠️ Every caller must include the LOCALE (`/en/cart`, not `/cart`). A path without
 * one is not broken — the proxy redirects it — but it costs a second round trip and
 * lands the shopper on the DEFAULT locale rather than the one they were reading.
 * Measured: a no-JS add from `/en/products/p` with `back=/products/p` answered
 * `303 Location: /products/p`.
 */
function safeBack(fd: FormData): string | null {
	const raw = str(fd, "back");
	return /^\/(?![/\\])/.test(raw) ? raw : null;
}

async function currentCart(): Promise<{ lines: ReturnType<typeof parseCartCookie> }> {
	const jar = await cookies();
	return { lines: parseCartCookie(jar.get(CART_COOKIE)?.value) };
}

async function writeCart(lines: ReturnType<typeof parseCartCookie>): Promise<void> {
	// ⚠️ `writeCartCookie` is the ONE writer — it enforces the 4KB budget and
	// expires any ancestor-domain duplicate, without which a cart can wedge until
	// the shopper clears their cookies (TRY-471). Never `jar.set(CART_COOKIE, …)`
	// here.
	await writeCartCookie(lines);
	// Both, and the cart page is not enough: the header's cart count renders on
	// every storefront page.
	revalidatePath("/", "layout");
}

/** Is the store taking orders at all? `viewOnly` in `(shop)/layout.tsx`'s terms. */
async function ordersRefused(): Promise<boolean> {
	const ctx = await getCurrentTenant();
	if (!ctx) return true;
	const settings = await ctx.db.storeSettings.findFirst({
		select: { isLive: true, catalogOnly: true },
	});
	return !(settings?.isLive ?? false) || (settings?.catalogOnly ?? false);
}

/**
 * ⚠️ **Every one of these returns `void`, and that is React's requirement rather than
 * a design choice**: `<form action={…}>` types the action as
 * `(fd: FormData) => void | Promise<void>`, so an action that answered
 * `{ ok, error }` could not be passed directly — and passing it directly is the whole
 * point, because `useActionState`'s wrapper is what withholds the hidden
 * `$ACTION_ID_*` input and makes a no-JS POST 500 (FORMS.md §11).
 *
 * So a refusal travels the only way it can without JavaScript: as a query parameter on
 * the redirect back. `cart=full` / `cart=refused` / `cart=invalid` / `cart=stock`. With JS the client
 * provider never reads any of it; it has already applied its optimistic update.
 *
 * ⚠️ So a JS refusal (no `back`) revalidates: the action's response is then the
 * correction. Without it the refused line stayed on screen until checkout's first
 * server render emptied the cart.
 */
function back(fd: FormData, error?: "full" | "refused" | "invalid" | "stock"): never | void {
	const to = safeBack(fd);
	if (!to) {
		if (error) revalidatePath("/", "layout");
		return;
	}
	redirect(error ? `${to}${to.includes("?") ? "&" : "?"}cart=${error}` : to);
}

/**
 * Does this add skip a selection the product REQUIRES — a variant, or an add-on
 * group with a minimum?
 *
 * Answers `false` (permit) when the product cannot be loaded: an unknown id is
 * already handled downstream by `computeOrder`, which drops the line, and
 * refusing here would turn a database hiccup into "the cart stopped working".
 */
async function selectionUnsatisfied(
	productId: string,
	variantId: string | undefined,
	addons: { gi: number; ci: number }[],
	note: string,
): Promise<boolean> {
	const ctx = await getCurrentTenant();
	if (!ctx) return false;
	const product = await ctx.db.product.findFirst({
		where: { id: productId, status: "published", deletedAt: null },
		select: { hasVariants: true, addons: true, noteRequired: true },
	});
	if (!product) return false;
	// A variant product with no variant chosen is the original `hasVariants` case.
	if (product.hasVariants && !variantId) return true;
	// A product whose note is mandatory (a name to engrave, a cake message) is
	// refused without one, on the same grounds as a skipped add-on group: the
	// listing card, the no-JS form and the WebMCP tool all post this action, so a
	// gate that only lives in `ProductDetail` leaves two other doors open.
	if (product.noteRequired && !note) return true;
	return addonSelectionInvalid(product.addons, addons);
}

type StoredLines = ReturnType<typeof parseCartCookie>;

/**
 * What checkout could take of each product/variant in `lines` (`availableStocks`
 * for the shopper's branch), read once per request. An empty map when the store
 * has stock tracking off, which is the decrement's own skip.
 */
async function stockCeilings(lines: StoredLines): Promise<Map<string, number | null>> {
	const ctx = await getCurrentTenant();
	if (!ctx || lines.length === 0) return new Map();
	const [settings, branch] = await Promise.all([
		ctx.db.storeSettings.findFirst({ select: { stockTrackingEnabled: true } }),
		getStorefrontBranchContext(ctx.db),
	]);
	if (!(settings?.stockTrackingEnabled ?? true)) return new Map();
	return availableStocks(
		ctx.db,
		branch.active?.id ?? null,
		lines.map((l) => ({ productId: l.p, variantId: l.v })),
	);
}

/**
 * Cap the line keyed `key` so the cart holds no more of its product/variant than
 * checkout could take, counting every OTHER line of the same product/variant (a
 * size can sit on several lines with different add-ons or notes, and checkout
 * decrements each). A line with no room left is dropped. `capped` says the cart
 * holds less than was asked for, which the caller reports as `cart=stock`.
 */
function capToStock(
	lines: StoredLines,
	key: string,
	ceilings: Map<string, number | null>,
): { lines: StoredLines; capped: boolean } {
	const target = lines.find((l) => storedLineKey(l) === key);
	const ceiling = target ? ceilings.get(stockKey(target.p, target.v)) : undefined;
	if (!target || ceiling == null) return { lines, capped: false };
	const others = lines.reduce(
		(n, l) => (l.p === target.p && l.v === target.v && storedLineKey(l) !== key ? n + l.q : n),
		0,
	);
	const room = Math.max(0, ceiling - others);
	if (target.q <= room) return { lines, capped: false };
	return {
		lines:
			room === 0
				? lines.filter((l) => storedLineKey(l) !== key)
				: lines.map((l) => (storedLineKey(l) === key ? { ...l, q: room } : l)),
		capped: true,
	};
}

export async function addToCartAction(fd: FormData): Promise<void> {
	const productId = str(fd, "productId");
	if (!productId) return back(fd, "invalid");
	if (await ordersRefused()) return back(fd, "refused");

	const qty = qtySchema.safeParse(str(fd, "qty") || "1");
	if (!qty.success || qty.data < 1) return back(fd, "invalid");

	let addons: z.infer<typeof addonsSchema> | undefined;
	const rawAddons = str(fd, "addons");
	if (rawAddons) {
		try {
			const parsed = addonsSchema.safeParse(JSON.parse(rawAddons));
			if (!parsed.success) return back(fd, "invalid");
			addons = parsed.data;
		} catch {
			return back(fd, "invalid");
		}
	}

	const variantId = str(fd, "variantId") || undefined;
	// ⚠️ A product that must be configured is REFUSED here, not merely discouraged
	// in the UI (TRY-469). The listing card, the no-JS form and the WebMCP
	// `add_to_cart` tool all post this action, so a client-side gate on the card
	// leaves two other doors open — and the line that gets in cannot be priced:
	// `computeOrder` returns null for the whole cart, so the shopper's cart renders
	// EMPTY and no click can change it.
	const note = str(fd, "note");
	if (await selectionUnsatisfied(productId, variantId, addons ?? [], note))
		return back(fd, "invalid");

	const { lines } = await currentCart();
	const line = {
		p: productId,
		v: variantId,
		q: qty.data,
		a: addons && addons.length > 0 ? addons : undefined,
		n: note || undefined,
	};
	const next = mergeLine(lines, line);
	// `mergeLine` caps rather than throws, so the cap is reported here — a cart that
	// silently ignores a click is worse than one that says it is full.
	if (next.length === lines.length && !lines.some((l) => storedLineKey(l) === storedLineKey(line)))
		return back(fd, "full");
	const stocked = capToStock(next, storedLineKey(line), await stockCeilings([line]));
	await writeCart(stocked.lines);
	back(fd, stocked.capped ? "stock" : undefined);
}

/**
 * Every line of one of the customer's past orders, back into the cart, in ONE
 * cookie write, then the cart.
 *
 * It used to be a client loop of N `addToCartAction` posts and a `router.push`
 * in the same tick, and that lost the order twice over: the posts carried no
 * add-ons (an `OrderItem` snapshots labels and prices, not the `gi`/`ci`
 * indices the cookie stores), so every product with a required group was
 * refused by `selectionUnsatisfied` and the survivors came back at their
 * defaults; and the navigation jumped Next's action queue, so `/cart` rendered
 * from a cookie holding at most one line. "Only one item, without its toppings".
 *
 * The indices are rebuilt here by matching each snapshotted add-on LABEL against
 * the product's current choices. A line whose product is gone, whose variant is
 * gone, or whose required group no longer has a matching choice is skipped
 * rather than added bare: a bare line would make `computeOrder` return null
 * for the WHOLE cart and the shopper would see an empty cart no click can fix
 * (the `addToCartAction` comment above). Gift lines (price 0, written by the
 * offer engine) are skipped too: the offer re-adds its own gift if it still
 * applies, and re-adding one as a paid line would charge for it.
 */
export async function reorderAction(fd: FormData): Promise<void> {
	const orderId = str(fd, "orderId");
	const ctx = await getCurrentTenant();
	const session = await getCustomerSession();
	if (!orderId || !ctx || !session || session.tenantId !== ctx.tenant.id)
		return back(fd, "invalid");
	if (await ordersRefused()) return back(fd, "refused");

	const order = await ctx.db.order.findFirst({
		where: { id: orderId, customerId: session.sub },
		include: { items: true },
	});
	if (!order) return back(fd, "invalid");

	const products = await ctx.db.product.findMany({
		where: {
			id: { in: order.items.flatMap((i) => (i.productId ? [i.productId] : [])) },
			status: "published",
			deletedAt: null,
		},
		select: {
			id: true,
			hasVariants: true,
			addons: true,
			noteRequired: true,
			variants: { select: { id: true } },
		},
	});
	const byId = new Map(products.map((p) => [p.id, p]));

	let { lines } = await currentCart();
	let capped = false;
	const picked: StoredLines = [];
	for (const it of order.items) {
		const product = it.productId ? byId.get(it.productId) : undefined;
		if (!product || Number(it.price) === 0) continue;
		const variantId = it.variantId ?? undefined;
		if (product.hasVariants && !(variantId && product.variants.some((v) => v.id === variantId)))
			continue;
		const wanted = OrderItemAddonsSchema.safeParse(it.addons ?? []);
		const picks = wanted.success ? wanted.data : [];
		const a = addonGroups(product.addons).flatMap((g, gi) =>
			(g.choices ?? []).flatMap((c, ci) =>
				picks.some((w) => w.label.en === c.labelEn && w.label.ar === c.labelAr) ? [{ gi, ci }] : [],
			),
		);
		if (addonSelectionInvalid(product.addons, a)) continue;
		// The merchant may have made the note mandatory since this order was
		// placed, and a line the shopper cannot see is a line they cannot fill in.
		if (product.noteRequired && !it.note) continue;
		const line = {
			p: product.id,
			v: variantId,
			q: Math.min(MAX_LINE_QTY, Math.max(1, it.qty)),
			a: a.length > 0 ? a : undefined,
			n: it.note ?? undefined,
		};
		picked.push(line);
	}
	// One stock read for the whole order, then each line merged and capped in turn,
	// so an earlier line's units count against a later line of the same variant.
	const ceilings = await stockCeilings(picked);
	for (const line of picked) {
		const stocked = capToStock(mergeLine(lines, line), storedLineKey(line), ceilings);
		lines = stocked.lines;
		capped ||= stocked.capped;
	}
	await writeCart(lines);
	back(fd, capped ? "stock" : undefined);
}

export async function setCartQtyAction(fd: FormData): Promise<void> {
	const key = str(fd, "key");
	const qty = qtySchema.safeParse(str(fd, "qty"));
	if (!key || !qty.success) return back(fd, "invalid");
	const { lines } = await currentCart();
	// ⚠️ A quantity INCREASE on a browse-only store is an add; a decrease is not.
	// Refusing both would strand a cart built before the merchant flipped the switch.
	const current = lines.find((l) => storedLineKey(l) === key);
	if (current && qty.data > current.q && (await ordersRefused())) return back(fd, "refused");
	const next = lines
		.map((l) => (storedLineKey(l) === key ? { ...l, q: qty.data } : l))
		.filter((l) => l.q > 0);
	// Only an increase can pass the shelf; a decrease must always land, even on a
	// line already over a stock that fell after it was added.
	const stocked =
		current && qty.data > current.q
			? capToStock(next, key, await stockCeilings([current]))
			: { lines: next, capped: false };
	await writeCart(stocked.lines);
	back(fd, stocked.capped ? "stock" : undefined);
}

export async function removeCartLineAction(fd: FormData): Promise<void> {
	const key = str(fd, "key");
	if (!key) return back(fd, "invalid");
	const { lines } = await currentCart();
	await writeCart(lines.filter((l) => storedLineKey(l) !== key));
	back(fd);
}

export async function clearCartAction(fd: FormData): Promise<void> {
	await writeCart([]);
	back(fd);
}

export interface CartLineInput {
	productId: string;
	variantId?: string;
	/** Lines with add-ons can't be price-compared (cart price includes add-ons). */
	hasAddons: boolean;
}

export interface CartLineCheck {
	available: boolean;
	reason?: "removed" | "out" | "unavailable";
	/** Current comparable unit price (null = not comparable / unchanged base). */
	priceNow: number | null;
	/**
	 * Units of this product/variant checkout could take (`availableStock`), shared by
	 * every line of it; `null` = no ceiling.
	 */
	stock: number | null;
}

/**
 * Re-validate cart lines against the live catalog so the cart can flag products
 * that changed since they were added (removed, out of stock, unavailable now,
 * a base price change for add-on-free lines, or fewer units left than the cart holds).
 */
export async function validateCartAction(lines: CartLineInput[]): Promise<CartLineCheck[]> {
	const ctx = await getCurrentTenant();
	if (!ctx) return lines.map(() => ({ available: true, priceNow: null, stock: null }));
	const { db } = ctx;

	const settings = await db.storeSettings.findFirst({
		select: { timezone: true, stockTrackingEnabled: true },
	});
	const tz = settings?.timezone || "Asia/Bahrain";
	const stockOn = settings?.stockTrackingEnabled ?? true;
	const branchId = (await getStorefrontBranchContext(db)).active?.id ?? null;

	const ids = [...new Set(lines.map((l) => l.productId))];
	const products = await db.product.findMany({
		where: { id: { in: ids } },
		include: {
			variants: { where: { deletedAt: null } },
			category: { select: { unavailable: true, unavailableUntil: true } },
		},
	});
	const byId = new Map(products.map((p) => [p.id, p]));

	// The same reader `capToStock` uses, so the cart warns at exactly the number
	// the add and the stepper stop at.
	const stockOf = stockOn
		? await availableStocks(db, branchId, lines)
		: new Map<string, number | null>();

	return lines.map((l) => {
		const stock = stockOf.get(stockKey(l.productId, l.variantId)) ?? null;
		const p = byId.get(l.productId);
		if (!p || p.deletedAt || p.status !== "published") {
			return { available: false, reason: "removed", priceNow: null, stock };
		}
		if (!isProductOrderableNow(p, tz)) {
			return { available: false, reason: "unavailable", priceNow: null, stock };
		}
		let priceNow: number | null = l.hasAddons ? null : Number(p.price);
		if (l.variantId) {
			const v = p.variants.find((x) => x.id === l.variantId);
			if (!v || !v.active) {
				return { available: false, reason: "removed", priceNow: null, stock };
			}
			priceNow = l.hasAddons ? null : Number(v.price);
		}
		if (stock === 0) {
			return { available: false, reason: "out", priceNow: null, stock };
		}
		return { available: true, priceNow, stock };
	});
}
